import Phaser from 'phaser';
import { sfx } from '../audio/SFX.js';

interface GameOverData {
  leaderboard?: Array<{
    playerId: string;
    coinsCollected: number;
    coinsBanked: number;
    isExtracted: boolean;
    kills: number;
  }>;
  mySnakeId?: string;
  coinsBanked?: number;
}

/** End-of-run tally screen with arcade-style results */
export class GameOverScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameOverScene' });
  }

  create(data: GameOverData): void {
    const { width, height } = this.scale;
    const cx = width / 2;

    // Background
    this.add.rectangle(cx, height / 2, width, height, 0x0a0a0a, 0.95);

    // Title
    const extracted = data.coinsBanked !== undefined && data.coinsBanked > 0;
    const title = extracted ? 'EXTRACTED!' : 'MATCH OVER';
    const titleColor = extracted ? '#00ff88' : '#ffffff';

    const titleText = this.add.text(cx, height * 0.1, title, {
      fontFamily: 'monospace',
      fontSize: '42px',
      color: titleColor,
      stroke: '#000',
      strokeThickness: 4,
    }).setOrigin(0.5).setAlpha(0);

    this.tweens.add({
      targets: titleText,
      alpha: 1,
      y: height * 0.12,
      duration: 400,
      ease: 'Back.easeOut',
      onComplete: () => sfx(this, 'title_slam'),
    });

    // Banked coins with punch animation
    if (data.coinsBanked !== undefined) {
      const bankedText = this.add.text(cx, height * 0.28, `BANKED: ${data.coinsBanked}`, {
        fontFamily: 'monospace',
        fontSize: '32px',
        color: '#ffdd00',
        stroke: '#000',
        strokeThickness: 3,
      }).setOrigin(0.5).setAlpha(0).setScale(0.5);

      this.tweens.add({
        targets: bankedText,
        alpha: 1,
        scale: 1,
        duration: 500,
        delay: 400,
        ease: 'Back.easeOut',
        onComplete: () => {
          this.cameras.main.shake(100, 0.008);
        },
      });
    }

    // Leaderboard
    if (data.leaderboard) {
      const startY = height * 0.4;

      this.add.text(cx, startY, '--- LEADERBOARD ---', {
        fontFamily: 'monospace',
        fontSize: '16px',
        color: '#666666',
      }).setOrigin(0.5);

      const headerY = startY + 25;
      this.add.text(cx - 150, headerY, 'RANK  NAME', {
        fontFamily: 'monospace', fontSize: '11px', color: '#555555',
      });
      this.add.text(cx + 60, headerY, 'COINS', {
        fontFamily: 'monospace', fontSize: '11px', color: '#555555',
      });
      this.add.text(cx + 120, headerY, 'KILLS', {
        fontFamily: 'monospace', fontSize: '11px', color: '#555555',
      });

      data.leaderboard.slice(0, 8).forEach((entry, i) => {
        const isMe = data.mySnakeId && entry.playerId === 'Player';
        const color = isMe ? '#00ff88' : '#cccccc';
        const rowY = headerY + 22 + i * 22;
        const status = entry.isExtracted ? ' $' : '';

        const row = this.add.text(cx - 150, rowY,
          `#${i + 1}  ${entry.playerId}${status}`, {
            fontFamily: 'monospace', fontSize: '13px', color,
          }).setAlpha(0);

        this.add.text(cx + 60, rowY, `${entry.coinsCollected}`, {
          fontFamily: 'monospace', fontSize: '13px', color: '#ffdd00',
        }).setAlpha(0);

        this.add.text(cx + 120, rowY, `${entry.kills}`, {
          fontFamily: 'monospace', fontSize: '13px', color: '#ff4444',
        }).setAlpha(0);

        // Staggered reveal
        this.tweens.add({
          targets: [row, this.children.list[this.children.list.length - 2], this.children.list[this.children.list.length - 1]],
          alpha: 1,
          duration: 200,
          delay: 600 + i * 100,
          onStart: () => {
            sfx(this, isMe ? 'leaderboard_player' : 'leaderboard_entry', { volume: 0.5 });
          },
        });
      });
    }

    // Play again button
    const playAgain = this.add.text(cx, height * 0.88, '[ PLAY AGAIN ]', {
      fontFamily: 'monospace',
      fontSize: '24px',
      color: '#ffffff',
      backgroundColor: '#00cc66',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setAlpha(0);

    this.tweens.add({
      targets: playAgain,
      alpha: 1,
      duration: 300,
      delay: 1500,
    });

    playAgain.on('pointerover', () => { sfx(this, 'button_hover'); playAgain.setStyle({ backgroundColor: '#00ff88' }); });
    playAgain.on('pointerout', () => playAgain.setStyle({ backgroundColor: '#00cc66' }));
    playAgain.on('pointerdown', () => {
      sfx(this, 'button_click');
      this.scene.start('MainMenuScene');
    });
  }
}
